import React, { useCallback, useEffect, useState } from "react"
import api from "services/api.service"
import { Row, Col } from "reactstrap"
import Loader from "react-loaders"
import colors from "utils/colors"
import { userService } from "services/user.service"
import isNullOrEmpty from "utils/isNullOrEmpty"
import { DynamicFormRadioButtonsViewOnly } from "../DynamicFormRadioButtons"
import { DynamicFormMultiSelectViewOnly } from "../DynamicFormMultiSelect"
import { DynamicFormSelectViewOnly } from "../DynamicFormSelect"
import { DynamicFormInputViewOnly } from "../DynamicFormInput"
import { DynamicFormTextareaViewOnly } from "../DynamicFormTextarea"
import { DynamicFormCurrencyViewOnly } from "../DynamicFormCurrency"
import { DynamicFormCheckboxViewOnly } from "../DynamicFormCheckbox"
import { DynamicFormDateTimeViewOnly } from "../DynamicFormDateTime"
import { DynamicFormLabel } from "../DynamicFormLabel"
import { DynamicFormText } from "../DynamicFormText"
import { DynamicFormDivider } from "../DynamicFormDivider"
import { DynamicFormRichTextViewOnly } from "../DynamicFormRichText"

const DynamicFormViewOnly = ({ formId, itemId, scenarioId }) => {
  const [form, setForm] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    if (formId !== undefined && formId != -1) {
      loadForm(signal)
    }

    return () => {
      controller.abort() // Cancels the previous request when effect runs again
    }
  }, [formId, itemId])

  const loadForm = async signal => {
    setIsLoading(true)

    try {
      let f = await api.getFormWithValues(formId, itemId ?? 0, scenarioId)
      if (!signal.aborted) {
        setForm(f)
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Failed to load form", error)
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false)
      }
    }
  }

  const col =
    form == null ? 12 : form.numColumns == 1 ? 12 : form.numColumns == 2 ? 6 : 4

  return (
    <React.Fragment>
      {isLoading && (
        <Loader
          type="line-scale-pulse-out"
          color={colors.primary}
          style={{ textAlign: "center" }}
        />
      )}
      {form != null && (
        <Row>
          <span id="form-loaded-indicator" style={{ visibility: "hidden" }}>
            Form Loaded
          </span>
          {form.formFields.map((ff, idx) => {
            return (
              <React.Fragment key={idx}>
                {ff.formFieldType && (
                  <>
                    {ff.formFieldType.hasOptions && (
                      <>
                        {ff.formFieldType.type == "Radio Button" && (
                          <DynamicFormRadioButtonsViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            formFieldListItems={ff.formFieldListItems}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}
                        {(ff.formFieldType.type == "Multi Select (System)" ||
                          ff.formFieldType.type == "Multi Select (Custom)") && (
                          <DynamicFormMultiSelectViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            formFieldListItems={ff.formFieldListItems}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}
                        {(ff.formFieldType.type == "Drop Down (Custom)" ||
                          ff.formFieldType.type == "Drop Down (System)") && (
                          <DynamicFormSelectViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            formFieldListItems={ff.formFieldListItems}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}
                      </>
                    )}
                    {!ff.formFieldType.hasOptions && (
                      <>
                        {(ff.formFieldType.type == "Short Text" ||
                          ff.formFieldType.type == "Signature" ||
                          ff.formFieldType.type == "Integer") && (
                          <DynamicFormInputViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "Long Text" && (
                          <DynamicFormTextareaViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {(ff.formFieldType.type == "Decimal" ||
                          ff.formFieldType.type == "Percentage" ||
                          ff.formFieldType.type == "Currency") && (
                          <DynamicFormCurrencyViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "CheckBox" && (
                          <DynamicFormCheckboxViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {(ff.formFieldType.type == "Date" ||
                          ff.formFieldType.type == "Time" ||
                          ff.formFieldType.type == "Date Time") && (
                          <DynamicFormDateTimeViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            type={
                              ff.formFieldType.type == "Date"
                                ? "date"
                                : ff.formFieldType.type == "Time"
                                ? "time"
                                : "datetime"
                            }
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "Header" && (
                          <DynamicFormLabel
                            label={ff.formFieldLabel}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "Help Text" && (
                          <DynamicFormText
                            text={ff.formFieldLabel}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "Divider" && (
                          <DynamicFormDivider
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}

                        {ff.formFieldType.type == "Rich Text" && (
                          <DynamicFormRichTextViewOnly
                            label={ff.formFieldLabel}
                            value={ff.fieldData.value}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )}
                        {/* {ff.formFieldType.type == "Documents" && (
                          <DynamicFormDocuments
                            label={ff.formFieldLabel}
                            itemId={itemId}
                            scenarioId={scenarioId}
                            col={col}
                            fullWidth={ff.fullWidth}
                          />
                        )} */}
                      </>
                    )}
                  </>
                )}
              </React.Fragment>
            )
          })}
        </Row>
      )}
    </React.Fragment>
  )
}

export default DynamicFormViewOnly
